/**
 * `<policy-outcome>` — `SC-F528`, the effective-outcome preview.
 *
 * It is mounted on the policy list beside the access review, because the two are
 * the same question asked at two scales: the review is "who can reach what across
 * this instance", this is "what happens to THIS subject on THESE tables, step by
 * step". An author debugging a denial asks the second; an auditor asks the first.
 *
 * # What this screen refuses to say, and why it says so loudly
 *
 * `03` §3.14 permits a Cloud-side mirror of engine logic only under three
 * obligations, and all three are visible in this template rather than assumed:
 *
 *  1. **The advisory label is in the panel header**, not a tooltip and not a
 *     footnote. §3.14 is explicit about the placement: "the moment an author trusts
 *     a preview more than the gateway is exactly the moment the mirror is wrong."
 *  2. **It never gates a publish**, and the sentence that says so also names what
 *     does — the pinned binary's own `config validate` and `policy validate`.
 *  3. **The drift status is per answer.** A JWT-shaped answer is covered by the
 *     golden-fixture corpus CI compares against the pinned binary; an API-key
 *     answer is not and cannot be, because `policy explain` hard-codes the JWT auth
 *     method (`SLUICE-U27`). The badge distinguishes them.
 *
 * Everything the preview cannot evaluate arrives as a NAMED cause — a CEL
 * condition, a claim comparison, a reject rule, a template, an API-key subject —
 * and the causes are grouped so a reader can see how much of the answer is unknown
 * before reading any of it. That vocabulary is `SC-F825`'s, deliberately: the
 * product has one word for "we cannot know this".
 *
 * Two things are load-bearing in the per-table chain. A **skipped** stage is drawn
 * differently from a stage that ran and did nothing, because an explicit deny
 * short-circuits and a mask on a query that never runs is not protection. And a
 * **shadow** object is badged and excluded from every protection count, because
 * counting an audit-mode mask as a mask would be the worst error this screen could
 * make.
 *
 * All four states: loading, empty (not built yet, or an instance with no
 * generation), error (the `apierr` code AND the request id), populated.
 */

import { LitElement, css, html, nothing, type TemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";

import "../components/index.js";
import type { BnBadgeVariant } from "../components/index.js";
import {
  getOutcomePreview,
  type IndeterminacyCause,
  type OutcomePreview,
  type PreviewFold,
  type PreviewMask,
  type PreviewStep,
  type PreviewTable,
  type ReviewPosture,
} from "../api/policies.js";
import { ApiError, parseError } from "../api/errors.js";

/** How each posture reads. The WORD is the encoding and the tone is a second
 * channel — identical to the access review's mapping, because the two artifacts
 * must not name the same answer two ways. */
const POSTURE: Record<ReviewPosture, { label: string; variant: BnBadgeVariant }> = {
  "denied-by-default": { label: "Nothing allowed this", variant: "neutral" },
  "denied-explicitly": { label: "A policy denied this", variant: "success" },
  "allow-authored": { label: "Allowed", variant: "warning" },
  conditional: { label: "Conditional", variant: "info" },
  indeterminate: { label: "Indeterminate", variant: "error" },
};

/** The heading each indeterminacy cause is grouped under. The vocabulary is
 * closed server-side, and an unknown cause falls back to its own token rather than
 * being dropped: a newer server naming a gap this bundle does not know must still
 * show the gap. */
const CAUSE_LABEL: Record<IndeterminacyCause, string> = {
  "cel-condition": "A CEL condition gates it",
  "claim-comparison": "A claim comparison gates it",
  "api-key-subject": "This subject shape cannot be verified",
  "reject-rule": "A reject rule is evaluated per statement",
  "row-filter-template": "A row filter resolves a template per request",
  "row-filter-expression": "A row filter is authored as CEL",
  "sql-action": "The policy is scoped to specific SQL actions",
  "unknown-tag": "A classification tag is undefined",
  "undecodable-object": "An object could not be read",
  "schema-cache": "The column set may be incomplete",
  approval: "A human decision is required",
};

@customElement("policy-outcome")
export class PolicyOutcome extends LitElement {
  @property() instanceId = "";

  @state() private preview: OutcomePreview | null = null;
  @state() private loading = false;
  @state() private opened = false;
  @state() private error: ApiError | null = null;
  @state() private user = "";
  @state() private groups = "";
  @state() private tables = "";
  @state() private apiKey = false;
  /** Which table's chain is expanded. One at a time: the chain is six stages of
   * prose and a wall of them answers nothing. */
  @state() private expanded = -1;

  private seq = 0;

  static override styles = css`
    :host {
      display: block;
      font-family: var(--bino-font-sans);
      color: var(--bino-text);
    }
    .lede,
    .meta {
      margin: 0;
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
      max-width: 82ch;
    }
    .lede {
      margin-bottom: var(--bino-space-md);
    }
    .advisory {
      display: flex;
      flex-wrap: wrap;
      align-items: baseline;
      gap: var(--bino-space-sm);
      margin: 0 0 var(--bino-space-sm);
      padding: var(--bino-space-sm);
      border: 1px solid var(--bino-warning-border);
      border-radius: var(--bino-radius);
      background: var(--bino-warning-bg);
      color: var(--bino-warning-text);
      font-size: var(--bino-font-size-sm);
      font-weight: 600;
      max-width: 82ch;
    }
    .advisory .detail {
      font-weight: 400;
    }
    .toolbar {
      display: flex;
      flex-wrap: wrap;
      align-items: flex-end;
      gap: var(--bino-space-md);
      margin-bottom: var(--bino-space-md);
    }
    .toolbar bn-field {
      margin-bottom: 0;
      flex: 1 1 12rem;
    }
    .stamp {
      display: flex;
      flex-wrap: wrap;
      gap: var(--bino-space-sm) var(--bino-space-md);
      margin-bottom: var(--bino-space-md);
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
    }
    ol.limits {
      margin: 0 0 var(--bino-space-lg);
      padding-left: 1.4em;
      display: grid;
      gap: var(--bino-space-xs);
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
      max-width: 82ch;
    }
    .rows {
      display: grid;
      gap: var(--bino-space-sm);
    }
    .row {
      border: 1px solid var(--bino-border);
      border-radius: var(--bino-radius);
      background: var(--bino-surface);
    }
    .row-head {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: var(--bino-space-sm) var(--bino-space-md);
      width: 100%;
      padding: var(--bino-space-md);
      background: none;
      border: 0;
      font: inherit;
      color: inherit;
      text-align: left;
      cursor: pointer;
      border-radius: var(--bino-radius);
    }
    .row-head:hover {
      background: var(--bino-surface-hover);
    }
    .row-head:focus-visible {
      outline: var(--bino-focus-ring);
      outline-offset: -2px;
    }
    .table-name {
      font-family: var(--bino-font-mono);
      flex: 1 1 16rem;
      min-width: 0;
      overflow-wrap: anywhere;
    }
    .chain {
      border-top: 1px solid var(--bino-border-light);
      padding: var(--bino-space-md);
      display: grid;
      gap: var(--bino-space-md);
    }
    .step {
      display: grid;
      gap: var(--bino-space-xs);
      padding-left: var(--bino-space-md);
      border-left: 3px solid var(--bino-border);
    }
    .step.skipped {
      border-left-style: dashed;
      opacity: 0.75;
    }
    .step-head {
      display: flex;
      flex-wrap: wrap;
      align-items: baseline;
      gap: var(--bino-space-sm);
    }
    .step-title {
      font-weight: 600;
      font-size: var(--bino-font-size-sm);
    }
    p.statement,
    p.why,
    p.note {
      margin: 0;
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
      max-width: 82ch;
    }
    p.why {
      color: var(--bino-text-muted);
      font-size: var(--bino-font-size-xs);
    }
    ul {
      margin: 0;
      padding: 0;
      list-style: none;
      display: grid;
      gap: var(--bino-space-xs);
      font-size: var(--bino-font-size-sm);
    }
    code {
      font-family: var(--bino-font-mono);
    }
    .fold {
      margin: 0;
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-base);
      background: var(--bino-surface-inset);
      border-radius: var(--bino-radius-sm);
      padding: var(--bino-space-xs) var(--bino-space-sm);
      overflow-x: auto;
    }
    .gaps {
      display: grid;
      gap: var(--bino-space-sm);
      margin-bottom: var(--bino-space-lg);
    }
    .gap {
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
      max-width: 82ch;
    }
    .gap strong {
      color: var(--bino-text);
    }
    .error-message {
      margin: 0 0 var(--bino-space-sm);
      color: var(--bino-error);
    }
  `;

  private async load() {
    const mine = ++this.seq;
    this.loading = true;
    this.opened = true;
    try {
      const res = await getOutcomePreview(this.instanceId, {
        subject: {
          user: this.user.trim(),
          groups: splitList(this.groups),
          auth_method: this.apiKey ? "api_key" : "jwt",
        },
        tables: splitList(this.tables),
      });
      if (mine !== this.seq) return;
      this.preview = res;
      this.error = null;
      this.expanded = res.tables.length === 1 ? 0 : -1;
    } catch (e) {
      if (mine !== this.seq) return;
      this.error = parseError(e);
      this.preview = null;
    } finally {
      if (mine === this.seq) this.loading = false;
    }
  }

  private renderError(err: ApiError) {
    return html`<div role="alert">
      <p class="error-message">${err.message}</p>
      <bn-kv
        .rows=${[
          { label: "Error code", value: err.code },
          ...(err.requestId ? [{ label: "Request id", value: err.requestId }] : []),
        ]}
      ></bn-kv>
      ${err.requestId
        ? nothing
        : html`<p class="meta">
            The request did not reach Sluice Cloud, so there is no request id to quote.
          </p>`}
      <bn-button variant="primary" @click=${() => void this.load()}>Try again</bn-button>
    </div>`;
  }

  /** The fold, as the ordered list plus the composed expression over policy names. */
  private renderFold(fold: PreviewFold): TemplateResult {
    return html`
      <p class="fold">${fold.expression}</p>
      <ul>
        ${fold.members.map(
          (m) => html`<li>
            ${m.position}. <code>${m.name}</code> — priority ${m.priority}, ${m.combine}
            ${m.shadow
              ? html`<bn-badge variant="audit">shadow — installs nothing</bn-badge>`
              : m.installs
                ? html`<bn-badge variant="neutral">installs the predicate</bn-badge>`
                : m.widens
                  ? html`<bn-badge variant="warning"
                      >OR — widens what the earlier filters allowed</bn-badge
                    >`
                  : html`<bn-badge variant="neutral">AND</bn-badge>`}
            ${m.templated ? html`<bn-badge variant="info">templated</bn-badge>` : nothing}
            ${m.expression ? html`<bn-badge variant="info">CEL</bn-badge>` : nothing}
          </li>`,
        )}
      </ul>
    `;
  }

  /** The masks, winner first with what it suppressed underneath it. */
  private renderMasks(masks: PreviewMask[]): TemplateResult {
    return html`<ul>
      ${masks.map(
        (m) => html`<li>
          <code>${m.column}</code> — ${m.winner.mask}, class ${m.winner.class_label}, via
          <code>${m.winner.policy}</code>
          ${m.suppressed && m.suppressed.length > 0
            ? html`<span class="why"
                >suppresses ${m.suppressed.map((s) => s.policy).join(", ")}</span
              >`
            : nothing}
          ${m.note ? html`<span class="why">${m.note}</span>` : nothing}
        </li>`,
      )}
    </ul>`;
  }

  private renderStep(step: PreviewStep, table: PreviewTable): TemplateResult {
    const live = (step.matches ?? []).filter((m) => !m.shadow);
    const shadows = (step.matches ?? []).filter((m) => m.shadow);
    return html`<div class="step ${step.skipped ? "skipped" : ""}">
      <div class="step-head">
        <span class="step-title">${step.title}</span>
        ${step.skipped ? html`<bn-badge variant="neutral">never runs</bn-badge>` : nothing}
        ${step.certainty !== "static"
          ? html`<bn-badge variant="info">${step.certainty}</bn-badge>`
          : nothing}
      </div>
      <p class="statement">${step.statement}</p>
      ${step.why ? html`<p class="why">${step.why}</p>` : nothing}
      ${live.length > 0
        ? html`<ul>
            ${live.map(
              (m) => html`<li>
                <code>${m.kind}/${m.name}</code> — ${m.effect}, priority ${m.priority}
                ${m.why ? html`<span class="why">${m.why}</span>` : nothing}
              </li>`,
            )}
          </ul>`
        : nothing}
      ${shadows.length > 0
        ? html`<ul>
            ${shadows.map(
              (m) => html`<li>
                <bn-badge variant="warning">Shadow</bn-badge>
                <code>${m.kind}/${m.name}</code> — ${m.effect} in ${m.enforcement} mode, so it
                records what it would have done and changes nothing.
              </li>`,
            )}
          </ul>`
        : nothing}
      ${step.stage === "row-filter" && table.fold ? this.renderFold(table.fold) : nothing}
      ${step.stage === "column-mask" && table.masks && table.masks.length > 0
        ? this.renderMasks(table.masks)
        : nothing}
      ${step.stage === "rewrite" && table.rewrite
        ? html`<bn-kv
            .rows=${[
              ...(table.rewrite.limit_max
                ? [{ label: "Row cap", value: String(table.rewrite.limit_max) }]
                : []),
              ...(table.rewrite.timeout ? [{ label: "Timeout", value: table.rewrite.timeout }] : []),
              ...(table.rewrite.sample ? [{ label: "Sample", value: table.rewrite.sample }] : []),
              ...(table.rewrite.policies
                ? [{ label: "From", value: table.rewrite.policies.join(", ") }]
                : []),
            ]}
          ></bn-kv>`
        : nothing}
    </div>`;
  }

  private renderTable(table: PreviewTable, index: number): TemplateResult {
    const meta = POSTURE[table.posture] ?? { label: table.posture, variant: "neutral" as const };
    const open = this.expanded === index;
    const maskCount = (table.masks ?? []).length;
    return html`<div class="row">
      <button
        class="row-head"
        aria-expanded=${open ? "true" : "false"}
        @click=${() => (this.expanded = open ? -1 : index)}
      >
        <span class="table-name">${table.table}</span>
        <bn-badge .variant=${meta.variant}>${meta.label}</bn-badge>
        ${table.fold && table.fold.expression
          ? html`<bn-badge variant="info">${table.fold.members.length} row filter(s)</bn-badge>`
          : nothing}
        ${maskCount > 0
          ? html`<bn-badge variant="info">${maskCount} masked column(s)</bn-badge>`
          : nothing}
        <span aria-hidden="true">${open ? "▾" : "▸"}</span>
      </button>
      ${open
        ? html`<div class="chain">
            ${table.steps.map((s) => this.renderStep(s, table))}
            ${table.notes && table.notes.length > 0
              ? html`<ul>
                  ${table.notes.map((n) => html`<li class="why">${n}</li>`)}
                </ul>`
              : nothing}
          </div>`
        : nothing}
    </div>`;
  }

  private renderGaps(preview: OutcomePreview): TemplateResult | typeof nothing {
    if (preview.indeterminacies.length === 0) return nothing;
    return html`<bn-panel eyebrow="What this preview cannot decide">
      <div class="gaps">
        ${preview.indeterminacies.map(
          (i) => html`<p class="gap">
            <strong>${CAUSE_LABEL[i.cause] ?? i.cause}</strong>${i.table
              ? html` · <code>${i.table}</code>`
              : nothing}${i.policy ? html` · <code>${i.policy}</code>` : nothing} — ${i.detail}
            ${i.upstream ? html` Tracked as ${i.upstream}.` : nothing}
          </p>`,
        )}
      </div>
    </bn-panel>`;
  }

  override render() {
    const preview = this.preview;
    return html`<bn-card heading="Effective-outcome preview">
      <p class="lede">
        What your authored policy set would do to one subject on one table, step by step through
        Sluice's fixed precedence chain — the access gate, the row-filter fold, the mask winner per
        column, the rewrite fold, the reject pass and the approval gate.
      </p>

      <div class="toolbar">
        <bn-field label="Subject id" hint="The value an apiKeys clause is compared against.">
          <bn-input
            mono
            .value=${this.user}
            placeholder="u-analyst"
            @input=${(e: CustomEvent<{ value: string }>) => (this.user = e.detail.value)}
          ></bn-input>
        </bn-field>
        <bn-field label="Groups" hint="Comma-separated. Supplying them is what lets a groups selector resolve here.">
          <bn-input
            .value=${this.groups}
            placeholder="analysts, finance"
            @input=${(e: CustomEvent<{ value: string }>) => (this.groups = e.detail.value)}
          ></bn-input>
        </bn-field>
        <bn-field
          label="Tables"
          hint="Comma-separated catalog.schema.table. Empty means every table the schema cache knows."
        >
          <bn-input
            mono
            .value=${this.tables}
            placeholder="warehouse.public.customers"
            @input=${(e: CustomEvent<{ value: string }>) => (this.tables = e.detail.value)}
          ></bn-input>
        </bn-field>
        <bn-checkbox
          label="API-key subject"
          hint="Changes which selectors can match — and marks the answer unverifiable, because policy explain cannot construct this shape (SLUICE-U27)."
          .checked=${this.apiKey}
          @change=${(e: CustomEvent<{ checked: boolean }>) => (this.apiKey = e.detail.checked)}
        ></bn-checkbox>
        <bn-button
          variant="primary"
          ?loading=${this.loading}
          ?disabled=${this.user.trim() === ""}
          @click=${() => void this.load()}
        >
          ${this.opened ? "Recompute" : "Compute the preview"}
        </bn-button>
      </div>

      ${this.error
        ? this.renderError(this.error)
        : this.loading && !preview
          ? html`<bn-spinner block size="28" label="Computing the preview"></bn-spinner>`
          : !preview
            ? html`<bn-empty-state
                icon="⤳"
                heading="Not computed yet"
                message="Name a subject and compute. The answer is advisory: it describes the configuration you authored and never evaluates a CEL condition, a claim comparison or a reject rule — where the outcome depends on one of those, it says so and names which policy is responsible."
              ></bn-empty-state>`
            : html`
                <!-- Obligation 1: the label is part of the header, never a tooltip. -->
                <p class="advisory" role="note">
                  ${preview.advisory}
                  <span class="detail">${preview.never_gates}</span>
                </p>
                <p class="advisory" role="note">
                  ${preview.verified.drift_tested
                    ? html`<bn-badge variant="success">Drift-tested against the pinned build</bn-badge>`
                    : html`<bn-badge variant="error">Not drift-testable</bn-badge>`}
                  <span class="detail">${preview.verified.statement}</span>
                </p>

                <div class="stamp">
                  <span
                    >Generation <strong>${preview.generation}</strong>
                    (${preview.generation_state})</span
                  >
                  <span>Subject <code>${preview.subject.user}</code></span>
                  <span>Action ${preview.action}</span>
                  <span>Pinned build ${preview.verified.sluice_commit.slice(0, 12)}</span>
                </div>

                <ol class="limits">
                  ${preview.limits.map((l) => html`<li>${l}</li>`)}
                </ol>

                ${preview.warnings && preview.warnings.length > 0
                  ? html`<bn-panel eyebrow="Conditions that narrow this answer">
                      <ul>
                        ${preview.warnings.map((w) => html`<li>${w}</li>`)}
                      </ul>
                    </bn-panel>`
                  : nothing}
                ${this.renderGaps(preview)}
                ${preview.tables.length === 0
                  ? html`<bn-empty-state
                      icon="∅"
                      heading="No table to preview"
                      message="Neither the schema cache nor the policy set names a table this preview could walk. Connect a datasource and introspect it, or name a table explicitly above."
                    ></bn-empty-state>`
                  : html`<div class="rows">
                      ${preview.tables.map((t, i) => this.renderTable(t, i))}
                    </div>`}
              `}
    </bn-card>`;
  }
}

/** splitList turns a comma-separated field into a trimmed list, or undefined when
 * it is empty — so an unset field is absent from the body rather than an empty
 * array the server would read as a narrowing to nothing. */
function splitList(raw: string): string[] | undefined {
  const parts = raw
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s !== "");
  return parts.length === 0 ? undefined : parts;
}

declare global {
  interface HTMLElementTagNameMap {
    "policy-outcome": PolicyOutcome;
  }
}
